import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ArrowDownLeft, ArrowUpRight, Phone, Repeat } from 'lucide-react';
import { useState } from 'react';
import { hasPermission, type SessionUser } from '@voice/shared';
import { Button, EmptyState, Notice, PageHeader, Select, Table, Tag } from '@voice/ui';
import { formatDateTime, formatDuration } from '../components/common';
import { api } from '../lib/api';
import { useSoftphone } from '../lib/softphone';
import type { CallRow, CallsResponse } from '../lib/types';
import styles from './Pages.module.css';

const RESULT: Record<string, { label: string; tone: 'ok' | 'warn' | 'busy' | 'neutral' }> = {
  answered: { label: 'Angenommen', tone: 'ok' },
  missed: { label: 'Verpasst', tone: 'busy' },
  busy: { label: 'Besetzt', tone: 'warn' },
  failed: { label: 'Fehlgeschlagen', tone: 'busy' },
  voicemail: { label: 'Nachricht', tone: 'neutral' },
  abandoned: { label: 'Aufgelegt', tone: 'warn' },
  rejected: { label: 'Abgewiesen', tone: 'warn' },
  message: { label: 'Nachricht', tone: 'neutral' },
};

export function CallLog({ user }: { user: SessionUser }) {
  const all = hasPermission(user.permissions, 'reports.view');
  const [scope, setScope] = useState<'me' | 'all'>(all ? 'all' : 'me');
  const [direction, setDirection] = useState('');
  const phone = useSoftphone();
  const path = scope === 'all' ? '/calls' : '/me/calls';
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const query = useQuery({
    queryKey: ['calls-history', scope, direction, page, size],
    queryFn: () => api<CallsResponse>(`${path}?limit=${size}&page=${page}${direction ? `&direction=${direction}` : ''}`),
    placeholderData: keepPreviousData,
  });
  const rows: CallRow[] = query.data?.calls ?? [];
  const pages = query.data?.pages ?? 1;
  const total = query.data?.total ?? 0;
  const from = total === 0 ? 0 : (page - 1) * size + 1;
  const to = Math.min(total, page * size);
  // Seitenzahlen mit Lücken: 1 … 4 5 [6] 7 8 … 20
  const numbers = Array.from(new Set([1, pages, ...Array.from({ length: 5 }, (_, i) => page - 2 + i)].filter((n) => n >= 1 && n <= pages))).sort((a, b) => a - b);
  const Icon = (c: CallRow) => (c.direction === 'inbound' ? ArrowDownLeft : c.direction === 'outbound' ? ArrowUpRight : Repeat);

  return (
    <div className={styles.page}>
      <PageHeader title="Anrufliste" />
      <div className={styles.inline} style={{ marginBottom: 20, alignItems: 'flex-end' }}>
        {all && (
          <Select label="Anzeigen" value={scope} onChange={(e) => { setScope(e.target.value as 'me' | 'all'); setPage(1); }}>
            <option value="all">Alle Anrufe</option>
            <option value="me">Nur meine</option>
          </Select>
        )}
        <Select label="Richtung" value={direction} onChange={(e) => { setDirection(e.target.value); setPage(1); }}>
          <option value="">Alle</option>
          <option value="inbound">Eingehend</option>
          <option value="outbound">Ausgehend</option>
          <option value="internal">Intern</option>
        </Select>
      </div>
      {query.isError && <Notice tone="error">Die Anrufliste konnte nicht geladen werden.</Notice>}
      {query.isSuccess && rows.length === 0 && <EmptyState>Noch keine Anrufe.</EmptyState>}
      {rows.length > 0 && (
        <Table aria-label="Anrufe">
          <thead>
            <tr>
              <th>Zeit</th>
              <th>Von</th>
              <th>An</th>
              <th>Ergebnis</th>
              <th>Dauer</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const I = Icon(c);
              const r = RESULT[c.disposition ?? 'failed'] ?? { label: c.disposition ?? '–', tone: 'neutral' as const };
              const other = c.direction === 'outbound' ? c.dialed : c.callerNumber;
              return (
                <tr key={c.id}>
                  <td>
                    <I size={14} aria-hidden="true" style={{ marginRight: 6 }} />
                    {formatDateTime(c.startedAt)}
                  </td>
                  <td>
                    {c.callerName ?? c.callerNumber}
                    {c.callerName && <div className={styles.muted}>{c.callerNumber}</div>}
                  </td>
                  <td>
                    {c.dialed}
                    {c.connectedNumber && c.connectedNumber !== c.dialed && <div className={styles.muted}>→ {c.connectedName ?? c.connectedNumber}</div>}
                  </td>
                  <td>
                    <Tag tone={r.tone}>{r.label}</Tag>
                  </td>
                  <td>{c.talkMs > 0 ? formatDuration(c.talkMs) : '–'}</td>
                  <td>
                    {phone.status === 'ready' && (
                      <button type="button" className={styles.iconBtn} onClick={() => void phone.dial(other)} aria-label={`${other} anrufen`}>
                        <Phone size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
      {total > 0 && (
        <nav className={styles.pager} aria-label="Seiten">
          <span className={styles.muted}>
            {from}–{to} von {total}
          </span>
          <div className={styles.inline}>
            <Button variant="secondary" onClick={() => setPage(page - 1)} disabled={page <= 1}>
              Zurück
            </Button>
            {numbers.map((n, i) => (
              <span key={n} className={styles.inline}>
                {i > 0 && n - numbers[i - 1]! > 1 && <span className={styles.muted}>…</span>}
                <Button variant={n === page ? 'primary' : 'secondary'} onClick={() => setPage(n)} aria-current={n === page ? 'page' : undefined}>
                  {n}
                </Button>
              </span>
            ))}
            <Button variant="secondary" onClick={() => setPage(page + 1)} disabled={page >= pages}>
              Weiter
            </Button>
          </div>
          <Select label="Pro Seite" value={String(size)} onChange={(e) => { setSize(Number(e.target.value)); setPage(1); }}>
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
        </nav>
      )}
    </div>
  );
}
